import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Repeat, UserCheck, Sparkles } from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/Reveal";
import { IMAGES } from "@/config";
import { staggerParent, fadeUp, viewportOnce } from "@/lib/anim";

const PILLARS = [
    {
        icon: ShieldCheck,
        title: "Controllo qualità interno",
        desc: "Ogni pulizia e ogni cambio biancheria è gestito dal nostro team, mai da ditte esterne: la qualità la garantiamo noi, di persona.",
    },
    {
        icon: Repeat,
        title: "Standard costanti",
        desc: "Lo stesso livello impeccabile a ogni soggiorno, senza sorprese. È ciò che trasforma un buon soggiorno in recensioni a 5 stelle.",
    },
    {
        icon: UserCheck,
        title: "Un unico responsabile",
        desc: "Un solo interlocutore che ha internalizzato l'intero processo: meno passaggi, meno rischi, più affidabilità per te.",
    },
];

export default function FamaLab() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <section
            id="fama-lab"
            ref={ref}
            className="relative scroll-mt-24 overflow-hidden bg-[var(--fama-ink)] py-24 lg:py-32"
            data-testid="fama-lab-section"
        >
            {/* subtle gold glow */}
            <div className="pointer-events-none absolute -right-40 top-0 h-[520px] w-[520px] rounded-full bg-[var(--fama-gold)]/10 blur-3xl" />

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
                {/* Image with spotlight + clipped frame */}
                <div className="relative order-2 lg:order-1">
                    <div className="relative aspect-[4/5] overflow-hidden rounded-[2px] ring-1 ring-[var(--fama-gold)]/40">
                        <motion.img
                            src={IMAGES.famaLab}
                            alt="Biancheria fresca e ambienti curati dal team FaMa Lab"
                            style={{ y: imgY }}
                            className="absolute inset-0 h-[116%] w-full object-cover"
                            loading="lazy"
                        />
                        <div className="pointer-events-none absolute inset-3 border border-white/30" />
                    </div>
                </div>

                {/* Text */}
                <div className="order-1 lg:order-2">
                    <FadeUp>
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--fama-gold)] text-[var(--fama-ink)]">
                                <Sparkles size={18} strokeWidth={1.8} />
                            </span>
                            <span className="font-body text-xs font-semibold uppercase tracking-[0.28em] text-[var(--fama-gold)]">
                                FaMa Lab · Brand interno
                            </span>
                        </div>
                    </FadeUp>

                    <MaskedLines
                        as="h2"
                        lines={["Pulizie e biancheria", "internalizzate.", "Mai affidate a terzi."]}
                        className="mt-6 font-serif-display text-4xl leading-[1.02] tracking-[-0.01em] text-[#fdfbf7] sm:text-5xl lg:text-[3.4rem]"
                    />

                    <FadeUp delay={0.1}>
                        <p className="mt-7 max-w-xl font-body text-base leading-relaxed text-[#fdfbf7]/75 sm:text-lg">
                            È il vero punto dolente di questo mestiere: pulizie e
                            biancheria affidate a fornitori esterni significano
                            qualità incostante e problemi difficili da
                            controllare. Noi abbiamo scelto la strada opposta.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.16}>
                        <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-[#fdfbf7]/75">
                            Con <strong className="text-[#fdfbf7]">FaMa Lab</strong>,
                            il nostro brand interno, l'intero processo è gestito
                            interamente da noi, dalla cura degli ambienti alla
                            biancheria certificata. Uno standard che per noi è la
                            norma e che, nel nostro settore, adottano in
                            pochissimi.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.22}>
                        <p className="mt-8 max-w-xl border-l-2 border-[var(--fama-gold)] pl-6 font-serif-display text-2xl italic leading-snug text-[#fdfbf7] sm:text-3xl">
                            Quando parliamo di gestione a 360°, intendiamo
                            esattamente questo: la qualità non la deleghiamo, la
                            garantiamo.
                        </p>
                    </FadeUp>

                    <motion.div
                        variants={staggerParent(0.12)}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                        className="mt-10 space-y-5"
                    >
                        {PILLARS.map((p) => {
                            const Icon = p.icon;
                            return (
                                <motion.div
                                    key={p.title}
                                    variants={fadeUp}
                                    className="flex gap-4 border-t border-white/10 pt-5"
                                >
                                    <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--fama-gold)]/50 text-[var(--fama-gold)]">
                                        <Icon size={18} strokeWidth={1.5} />
                                    </span>
                                    <div>
                                        <h3 className="font-serif-display text-xl text-[#fdfbf7]">
                                            {p.title}
                                        </h3>
                                        <p className="mt-1 font-body text-sm leading-relaxed text-[#fdfbf7]/65">
                                            {p.desc}
                                        </p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
